/**
 * Shared option constants for the custom vendor profile editor (VendorProfilePage).
 *
 * These are intentionally defined in code (Path B) rather than pulled from the
 * Sharetribe Console listing-field config, because vendor services/counties live
 * on the *user* and are edited through our own UI.
 *
 * IMPORTANT — keep these values in sync with the matching logic:
 *  - SERVICE_OPTIONS keys must each appear in the corresponding category's
 *    expansion in `server/api/matched-vendors.js` (getCategoryToServicesMap),
 *    otherwise a vendor offering the service won't match requests for it.
 *  - COUNTY_OPTIONS keys must be the *normalized* county form produced by
 *    `extractCountyFromAddress`/`normalizeCountyName` in `src/util/countyHelpers.js`
 *    (e.g. 'el_dorado'), because that is what request matching compares against.
 */

// Service keys aligned to canonical slugs in server/api/matched-vendors.js
export const SERVICE_OPTIONS = [
  { key: 'tree_service', label: 'Tree Service' },
  { key: 'brush_clearing', label: 'Brush Clearing' },
  { key: 'chipping', label: 'Chipping' },
  { key: 'debris_hauling', label: 'Debris Hauling' },
  { key: 'defensible_space', label: 'Defensible Space Clearing' },
  { key: 'fire_resistant_landscaping', label: 'Fire-Resistant Landscaping' },
  { key: 'grazing', label: 'Grazing' },
  { key: 'home_hardening', label: 'Home Hardening' },
  { key: 'heavy_machinery', label: 'Heavy Machinery' },
  { key: 'consultation', label: 'Consultation & Assessments' },
];

// County keys are the normalized form used by request matching.
export const COUNTY_OPTIONS = [
  { key: 'nevada', label: 'Nevada County' },
  { key: 'placer', label: 'Placer County' },
  { key: 'el_dorado', label: 'El Dorado County' },
  { key: 'yuba', label: 'Yuba County' },
  { key: 'sierra', label: 'Sierra County' },
  { key: 'butte', label: 'Butte County' },
  { key: 'sacramento', label: 'Sacramento County' },
];

// Banner gradient themes for vendors who don't upload a banner image.
// `gradient` is a ready-to-use CSS background value.
export const BANNER_THEMES = [
  { key: 'forest', label: 'Forest', gradient: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 55%, #40916c 100%)' },
  { key: 'sunset', label: 'Sunset', gradient: 'linear-gradient(135deg, #7f1d1d 0%, #b45309 55%, #f59e0b 100%)' },
  { key: 'slate', label: 'Slate', gradient: 'linear-gradient(135deg, #1e293b 0%, #334155 55%, #64748b 100%)' },
  { key: 'sage', label: 'Sage', gradient: 'linear-gradient(135deg, #52796f 0%, #84a98c 55%, #cad2c5 100%)' },
];

export const DEFAULT_BANNER_THEME = 'forest';

const optionLabel = (options, key) => options.find(o => o.key === key)?.label;

/** Human-friendly label for a stored service key (falls back to title-cased key). */
export const getServiceLabel = key =>
  optionLabel(SERVICE_OPTIONS, key) ||
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

/** Human-friendly label for a stored county key (falls back to title-cased key). */
export const getCountyLabel = key =>
  optionLabel(COUNTY_OPTIONS, key) ||
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

/** CSS gradient for a banner theme key (falls back to the default theme). */
export const getBannerGradient = key =>
  BANNER_THEMES.find(t => t.key === key)?.gradient ||
  BANNER_THEMES.find(t => t.key === DEFAULT_BANNER_THEME).gradient;
